import { useState, useEffect, useCallback, useContext, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import { PERMISSIONS, PERM_VIEW, permLabel, isEmailLike } from "./docSharing";

const styles = {
  dialogBox: { padding: "20px", paddingTop: "4px", minWidth: { sm: "480px" } },
  closeIcon: { position: "absolute", right: 8, top: 12, color: (theme) => theme.palette.grey[500] },
  shareRow: { display: "flex", gap: "8px", alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } },
  permSelect: { minWidth: 140 },
  permOption: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
  permDescription: { fontSize: "0.75rem", opacity: 0.7 },
  hint: { fontSize: "0.8rem", opacity: 0.7, marginTop: "8px" },
  accessRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "48px",
    padding: "4px 0",
    borderTop: "1px solid var(--color-border-primary, rgba(128,128,128,0.2))",
  },
  accessWho: { flex: 1, minWidth: 0, overflow: "hidden" },
  accessEmail: { fontSize: "0.8rem", opacity: 0.7, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" },
  sectionLabel: { fontWeight: "bold", margin: "20px 0 4px" },
  // The app themes through CSS variables while MUI's palette stays light, so
  // MUI's grey defaults vanish on the dark theme; take the theme's own colors.
  chip: { background: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" },
  chipOutlined: { borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" },
  shareButton: {
    "&.Mui-disabled": { background: "var(--color-bg-tertiary)", color: "var(--color-text-tertiary)" },
  },
  confirmRemove: { color: "#fff", "&:hover": { color: "#fff" } },
  // Anchored near the top, below the toasts: the dialog grows as people are
  // added and errors show, and a centered one jumps under the pointer each time.
  dialog: {
    "& .MuiDialog-container": { alignItems: "flex-start" },
    "& .MuiDialog-paper": { marginTop: "max(140px, 12vh)" },
  },
};

const shareErrorMessage = (res, toEmail) => {
  if (res?.status === 404) return `No Tapestry account uses ${toEmail}. Ask them to sign up first.`;
  if (res?.status === 409) return `${toEmail} already has access or a pending invitation.`;
  if (res?.status === 403) return "Only the owner can share this document.";
  if (res?.status === 400 && /yourself/i.test(res?.data?.detail || "")) return "That's your own account.";
  return "Couldn't send the invitation. Try again.";
};

const accessChipLabel = (share) =>
  share.status === "pending" ? `Invited · ${permLabel(share.permission)}` : permLabel(share.permission);

/**
 * Share a document with another Tapestry user (TT-807, backend TT-693).
 *
 * Email + permission invitation form, plus the owner's view of who already
 * has (or is invited to) access, with a confirmed remove. Distinct from
 * ShareModal.js, which shares tapestry images and links.
 */
const DocShareModal = ({ open, handleClose, doc, showSnackbar }) => {
  const { userInfo } = useContext(RootContext);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState(PERM_VIEW);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  // The doc the modal is showing now. The list page reuses one modal across
  // docs, so a slow access list for the last doc must not land on this one.
  const shownDocId = useRef(null);
  shownDocId.current = doc?.docId;

  const fetchShares = useCallback(async () => {
    const docId = doc?.docId;
    if (!userInfo?.username || !docId) return;
    setLoadingShares(true);
    const res = await AppBackService.getDocShares(userInfo.username, docId);
    if (shownDocId.current !== docId) return;
    setShares(res && !res.error ? res.shares || [] : []);
    setLoadingShares(false);
  }, [doc?.docId, userInfo?.username]);

  useEffect(() => {
    if (open) {
      setShares([]);
      setEmail("");
      setPermission(PERM_VIEW);
      setError("");
      setConfirmingId(null);
      fetchShares();
    }
  }, [open, fetchShares]);

  const submitShare = useCallback(async () => {
    const toEmail = email.trim();
    if (!toEmail || !userInfo?.username || !doc?.docId || sharing) return;
    if (!isEmailLike(toEmail)) {
      setError("Enter a full email address.");
      return;
    }
    setError("");
    setSharing(true);
    const res = await AppBackService.shareDoc(userInfo.username, doc.docId, {
      toUserEmail: toEmail,
      permission,
    });
    setSharing(false);
    if (res && !res.error) {
      showSnackbar(`Invitation sent to ${toEmail}`, "success");
      setEmail("");
      fetchShares();
    } else {
      setError(shareErrorMessage(res, toEmail));
    }
  }, [doc?.docId, email, fetchShares, permission, sharing, showSnackbar, userInfo?.username]);

  const revoke = useCallback(
    async (share) => {
      if (!userInfo?.username || !doc?.docId) return;
      setRevokingId(share.id);
      const res = await AppBackService.revokeDocShare(userInfo.username, doc.docId, share.toUserId);
      setRevokingId(null);
      setConfirmingId(null);
      const who = share.toUserName || share.toUserEmail || "them";
      if (res && !res.error) {
        const done = share.status === "pending" ? `Invitation to ${who} cancelled` : `${who} no longer has access`;
        showSnackbar(done, "success");
        fetchShares();
      } else {
        showSnackbar("Couldn't remove access. Try again.", "error");
      }
    },
    [doc?.docId, fetchShares, showSnackbar, userInfo?.username]
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={styles.dialog}>
      <DialogTitle>
        Share "{doc?.title || "document"}"
        <IconButton aria-label="close" onClick={handleClose} sx={styles.closeIcon}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box sx={styles.dialogBox}>
        <Box sx={styles.shareRow}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && submitShare()}
            error={!!error}
            helperText={error || " "}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            select
            label="Permission"
            value={permission}
            onChange={(e) => setPermission(+e.target.value)}
            SelectProps={{ renderValue: (value) => permLabel(value) }}
            size="small"
            sx={styles.permSelect}>
            {PERMISSIONS.map((p) => (
              <MenuItem key={p.value} value={p.value} sx={styles.permOption}>
                <span>{p.label}</span>
                <Typography component="span" sx={styles.permDescription}>
                  {p.description}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={submitShare} disabled={sharing || !email.trim()} sx={styles.shareButton}>
            {sharing ? "Sharing..." : "Share"}
          </Button>
        </Box>
        <Typography sx={styles.hint}>
          They get an email invitation, and the document shows up in their list once they accept.
        </Typography>

        <Typography sx={styles.sectionLabel}>People with access</Typography>
        <Box sx={styles.accessRow}>
          <Box sx={styles.accessWho}>
            <Typography>You</Typography>
          </Box>
          <Chip size="small" label="Owner" sx={styles.chip} />
        </Box>
        {loadingShares ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
            <CircularProgress size={22} aria-label="loading people with access" />
          </Box>
        ) : !shares.length ? (
          <Typography sx={styles.hint}>Only you can see this document.</Typography>
        ) : (
          shares.map((share) => {
            const pending = share.status === "pending";
            const confirming = confirmingId === share.id;
            return (
              <Box key={share.id} sx={styles.accessRow} data-testid="share-access-row">
                <Box sx={styles.accessWho}>
                  <Typography>{share.toUserName || share.toUserEmail}</Typography>
                  {share.toUserEmail && share.toUserName !== share.toUserEmail && (
                    <Typography sx={styles.accessEmail}>{share.toUserEmail}</Typography>
                  )}
                </Box>
                {confirming ? (
                  <>
                    <Button size="small" onClick={() => setConfirmingId(null)} disabled={revokingId === share.id}>
                      Keep
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      sx={styles.confirmRemove}
                      onClick={() => revoke(share)}
                      disabled={revokingId === share.id}>
                      {pending ? "Cancel invite" : "Remove"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Chip
                      size="small"
                      label={accessChipLabel(share)}
                      variant={pending ? "outlined" : "filled"}
                      sx={pending ? styles.chipOutlined : styles.chip}
                    />
                    <IconButton
                      aria-label={pending ? "cancel invitation" : "remove access"}
                      title={pending ? "Cancel invitation" : "Remove access"}
                      onClick={() => setConfirmingId(share.id)}>
                      <PersonRemoveIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Dialog>
  );
};

DocShareModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  doc: PropTypes.object,
  showSnackbar: PropTypes.func,
};

export default DocShareModal;
